import { SearchOutlined } from '@ant-design/icons';
import { useOnClickOutside } from '@shared/hooks/use-on-click-outside';
import {
  Badge,
  Button,
  Checkbox,
  CheckboxProps,
  ConfigProvider,
  DatePicker,
  DatePickerProps,
  Input,
  Popover,
  Radio,
  RadioProps,
  Skeleton,
  Tag,
} from 'antd';
import { CheckboxGroupProps } from 'antd/es/checkbox';
import { RangePickerProps } from 'antd/es/date-picker';
import { InputProps } from 'antd/lib';
import clsx from 'clsx';
import { Dispatch, ReactNode, SetStateAction, forwardRef, useRef, useState } from 'react';
import { RiArrowDownSLine, RiArrowRightSLine, RiFilterFill } from 'react-icons/ri';
import { useSearchParams } from 'react-router-dom';
import { GoDotFill } from 'react-icons/go';
import dayjs from 'dayjs';
import { disableFutureDates } from '@shared/utils/dayjs';
import { capitalize } from '@shared/utils/capitalize';
import { useTranslation } from 'react-i18next';
import { getCallingCodeFromAlpha2Code } from '@shared/utils/country.helper';

type Props = {
  filters: {
    label: string;
    loading?: boolean;
    icon?: JSX.Element;
    header?: JSX.Element;
    children?: JSX.Element;
  }[];
  isFilterOpen: boolean;
  setFilterOpen: Dispatch<SetStateAction<boolean>>;
  showAppliedFilter?: boolean;
  children?: JSX.Element;
  filterRightElement?: JSX.Element;
};

export function AppFilter(props: Props) {
  const { filters, isFilterOpen, setFilterOpen, children, showAppliedFilter = true, filterRightElement } = props;
  const [showItemIndex, setShowItemIndex] = useState(0);
  const filter = filters[showItemIndex];
  const { children: filterChildren, header, loading } = filter;

  const filterRef = useRef(null);
  useOnClickOutside(filterRef, function closeFilterDropdown() {
    setFilterOpen(false);
  });

  return (
    <div className="flex flex-col xl:flex-row gap-2 max-w-full w-full">
      <div className="relative pr-2 flex gap-3 justify-between w-full">
        <div className="filter-left-element flex items-center gap-3 flex-1 md:overflow-hidden">
          <div className="max-w-37.5">{children}</div>
          {showAppliedFilter ? (
            <div className="hidden xl:block flex-1 overflow-auto border-l border-gray-600 pl-3">
              <AppliedFilters />
            </div>
          ) : null}
        </div>
        <div className="filter-right-element">{filterRightElement}</div>

        <div
          ref={filterRef}
          className={clsx(
            isFilterOpen ? 'flex' : 'hidden',
            'absolute z-10 top-11',
            'divide-x divide-gray-700',
            'rounded-md',
            'border border-gray-700 shadow-xl',
            'animate-appear-top-xs h-77.5'
          )}
        >
          <section className="bg-gray-100 rounded-l-md p-1 md:min-w-56">
            {filters.map((filter, i) => (
              <MenuItem
                key={i}
                icon={filter.icon}
                label={filter.label}
                suffix={<RiArrowRightSLine size={20} />}
                onClick={() => setShowItemIndex(i)}
                isActive={showItemIndex === i}
                showSuffix={showItemIndex === i}
              />
            ))}
          </section>
          <section id="filter-panel" className="bg-surface w-52 md:w-auto md:min-w-52 rounded-r-lg flex flex-col pb-2">
            {header}
            <div className="p-1 space-y-1 overflow-y-auto flex-1">{loading ? <Loader /> : filterChildren}</div>
          </section>
        </div>
      </div>
      {showAppliedFilter ? (
        <div className="xl:hidden">
          <AppliedFilters />
        </div>
      ) : null}
    </div>
  );
}

export function FilterButton(props: {
  isFilterOpen: boolean;
  setFilterOpen: (value: boolean) => void;
  appliedFilterCount: number;
}) {
  const { isFilterOpen, setFilterOpen, appliedFilterCount = 0 } = props;
  const { t } = useTranslation();
  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            defaultHoverBg: 'var(--color-primary-50)',
            colorPrimaryBg: 'var(--color-primary-50)',
            colorPrimary: 'var(--color-text-600)',
            colorPrimaryBgHover: 'var(--color-primary-100)',
          },
        },
      }}
    >
      <Button
        icon={<RiFilterFill color="var(--color-primary)" size={16} />}
        variant={isFilterOpen ? 'filled' : 'outlined'}
        color={isFilterOpen ? 'primary' : 'default'}
        onClick={() => setFilterOpen(true)}
        // Opening the filter panel only flips local state; let it through the
        // demo's click guard (src/modules/demo/components/demo-interaction-guard.tsx).
        data-demo-interactive
      >
        <span className="flex gap-2 text-text-600">
          <p className="text-md">{capitalize(t('add_filter_one', { count: 1 }))}</p>
          <Badge
            style={{ boxShadow: 'unset', backgroundColor: 'var(--color-primary-400)' }}
            count={appliedFilterCount}
          />
        </span>
      </Button>
    </ConfigProvider>
  );
}
export function FilterButtonText(props: {
  isFilterOpen: boolean;
  setFilterOpen: (value: boolean) => void;
  appliedFilterCount?: number;
  className?: string;
  children?: ReactNode;
  icon?: ReactNode;
  badgeStyle?: React.CSSProperties;
}) {
  const {
    isFilterOpen,
    setFilterOpen,
    appliedFilterCount = 0,
    className = '',
    children,
    icon = <RiArrowDownSLine className="text-text-600! size-3.5" />,
    badgeStyle,
  } = props;

  const defaultBadgeStyle: React.CSSProperties = {
    boxShadow: 'unset',
    backgroundColor: 'var(--color-primary-400)',
    fontSize: 12,
    ...badgeStyle,
  };
  const { t } = useTranslation();

  return (
    <Button
      onClick={() => setFilterOpen(!isFilterOpen)}
      className={`bg-surface! h-7! rounded-4xl! flex items-center uppercase! gap-1 text-text-600! font-semibold! text-xs! ${className}`}
    >
      {children || <span>{t('filter_by')}</span>}
      {appliedFilterCount > 0 ? <Badge size="small" style={defaultBadgeStyle} count={appliedFilterCount} /> : null}
      {icon}
    </Button>
  );
}

export function MenuItem(props: {
  icon?: ReactNode;
  label: string | JSX.Element;
  onClick?: VoidFunction;
  isActive?: boolean;
  suffix?: ReactNode;
  showSuffix?: boolean;
  countryCode?: string | null;
}) {
  const { icon, label, suffix, onClick, isActive = false, showSuffix = true, countryCode } = props;
  const itemRef = useRef(null);
  const callingCode = countryCode ? getCallingCodeFromAlpha2Code(countryCode) : undefined;

  return (
    <div
      ref={itemRef}
      onClick={onClick}
      className={clsx(
        'flex items-center justify-between',
        'cursor-pointer p-2 rounded-md',
        isActive ? 'bg-primary-75 text-primary-500!' : 'text-text-600 hover:bg-gray-200'
      )}
    >
      <div className="flex items-center gap-1.5 w-full">
        <div className="[&>svg]:size-4.5">{icon}</div>
        {callingCode ? <div>+{callingCode}</div> : null}
        <p className="grow text-sm">{label}</p>
      </div>
      {showSuffix ? suffix : undefined}
    </div>
  );
}

function SearchInput(props: InputProps) {
  const { t } = useTranslation();
  return (
    <Input
      placeholder={capitalize(t('search'))}
      className="md:min-w-62.5! mt-1"
      {...props}
      size="large"
      prefix={<SearchOutlined size={18} />}
      variant="borderless"
    />
  );
}

function FilterRadio(props: { options: { label: RadioProps['title']; value: RadioProps['value'] }[] } & RadioProps) {
  const { options, ...restProps } = props;

  return (
    <Radio.Group style={{ width: '100%' }} {...restProps}>
      {options.map((option, i) => (
        <MenuItem
          isActive={false}
          key={i}
          label={
            <Radio value={option.value} className="w-full">
              {option.label}
            </Radio>
          }
        />
      ))}
    </Radio.Group>
  );
}

function FilterCheckbox(
  props: {
    options:
      | {
          label: ReactNode | null;
          value: CheckboxProps['value'];
        }[]
      | undefined;
    hasNextPage?: boolean;
    loadNextRef?: (node?: Element | null | undefined) => void;
  } & CheckboxGroupProps
) {
  const { options = [], hasNextPage, loadNextRef, ...restProps } = props;

  return (
    <Checkbox.Group style={{ width: '100%' }} {...restProps}>
      <div className="w-full">
        {options.map((option, i) => {
          const lastItem = options.length === i + 1;

          return (
            <div key={i}>
              <MenuItem
                isActive={false}
                label={
                  <Checkbox value={option.value} className="w-full text-sm! text-text-600! capitalize!">
                    {option.label}
                  </Checkbox>
                }
              />
              {lastItem && hasNextPage ? <Loader count={1} ref={loadNextRef} /> : null}
            </div>
          );
        })}
      </div>
    </Checkbox.Group>
  );
}

function FilterDate(props: DatePickerProps) {
  return (
    <DatePicker
      {...props}
      rootClassName="border border-gray-400 rounded-r-md"
      getPopupContainer={(node) => document.getElementById('filter-panel') ?? node}
      open
      showNow={false}
      popupAlign={{
        offset: [-4, -37],
      }}
      superNextIcon={null}
      superPrevIcon={null}
      disabledDate={(current) => current && current > dayjs().endOf('day')}
    />
  );
}

function FilterRangeDate(props: RangePickerProps) {
  const { RangePicker } = DatePicker;

  return (
    <RangePicker
      {...props}
      rootClassName="border border-gray-400 rounded-r-md"
      getPopupContainer={(node) => document.getElementById('filter-panel') ?? node}
      superNextIcon={null}
      superPrevIcon={null}
      disabledDate={disableFutureDates}
      popupAlign={{
        offset: [-4, -37],
      }}
      popupStyle={window.innerWidth < 768 ? { left: 0 } : { left: 224 }}
    />
  );
}

function AppliedFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const appliedFiltersIterator = searchParams.entries();
  const appliedFilters = Array.from(appliedFiltersIterator).filter(
    ([key]) => key !== 'tab' && key !== 'q' && key !== 'name'
  );
  const moreTags = appliedFilters.splice(3);
  const renderFilterTags = (filters: [string, string][]) =>
    filters.map((filter) => {
      const [key, value] = filter;
      return (
        <Tag
          key={value}
          closable
          onClose={() => {
            searchParams.delete(key, value);
            setSearchParams(searchParams);
          }}
        >
          {value.split(',')[1]}
        </Tag>
      );
    });

  function resetFilter() {
    const keepParams = ['tab', 'q', 'name'];
    const newSearchParams = new URLSearchParams();
    keepParams.forEach((param) => {
      const value = searchParams.get(param);
      if (value !== null) {
        newSearchParams.set(param, value);
      }
    });
    setSearchParams(newSearchParams);
  }

  if (appliedFilters?.length === 0) return;
  return (
    <div className="flex items-center overflow-auto max-w-full">
      <ConfigProvider
        theme={{
          components: {
            Tag: {
              lineHeightSM: 2.5,
            },
          },
        }}
      >
        {renderFilterTags(appliedFilters)}
      </ConfigProvider>
      <Popover placement="bottom" content={renderFilterTags(moreTags)}>
        <p className={clsx('whitespace-nowrap', moreTags.length ? 'block text-primary-400' : 'hidden')}>
          +{moreTags.length} {capitalize(t('more'))}
        </p>
      </Popover>
      <p
        onClick={resetFilter}
        className={clsx(
          'whitespace-nowrap',
          appliedFilters.length
            ? 'display cursor-pointer text-primary-400 ml-2 flex items-center gap-1 text-sm'
            : 'hidden'
        )}
      >
        <GoDotFill size={10} />
        {capitalize(t('reset_filters'))}
      </p>
    </div>
  );
}

type LoaderProps = {
  count?: number;
};

export const Loader = forwardRef<HTMLDivElement, LoaderProps>(function Loader({ count = 3 }, ref) {
  return (
    <div className="p-2 space-y-3" ref={ref}>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} active paragraph={false} />
      ))}
    </div>
  );
});
AppFilter.Loader = Loader;
AppFilter.Radio = FilterRadio;
AppFilter.MenuItem = MenuItem;
AppFilter.Button = FilterButton;
AppFilter.DatePicker = FilterDate;
AppFilter.FilterButtonText = FilterButtonText;
AppFilter.SearchInput = SearchInput;
AppFilter.Checkbox = FilterCheckbox;
AppFilter.RangePicker = FilterRangeDate;
